import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-mui";
import { useState } from "react";
import toast from "react-hot-toast";
import {
  api,
  type TrainingDataExportFilters,
} from "../../../api/client.ts";
import CopyButton from "../../../components/CopyButton.tsx";
import StatusChip from "../../../components/StatusChip.tsx";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import { formatNumber } from "../../../utils/format.ts";
import FormattedDate from "../../../components/FormattedDate.tsx";
import { keys } from "../../../api/queryKeys.ts";
import { isJobActive } from "../../../utils/status.ts";
import PageHeader from "../../../components/PageHeader.tsx";

function TrainingDataExportsPage() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [maxRowsStr, setMaxRowsStr] = useState("");
  const [countriesStr, setCountriesStr] = useState("");

  const { data: exports, isLoading } = useQuery({
    queryKey: keys.trainingDataExport.list(),
    queryFn: api.listTrainingDataExports,
    meta: { live: true },
  });

  const createMutation = useMutation({
    mutationFn: (filters: TrainingDataExportFilters) =>
      api.createTrainingDataExport({ filters }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.trainingDataExport.list() });
      toast.success("Export started");
      setCreateOpen(false);
      setMaxRowsStr("");
      setCountriesStr("");
    },
  });

  const handleCreate = () => {
    const filters: TrainingDataExportFilters = {};
    if (maxRowsStr.trim()) {
      const n = Number.parseInt(maxRowsStr, 10);
      if (Number.isFinite(n) && n > 0) filters.max_rows = n;
    }
    if (countriesStr.trim()) {
      filters.countries = countriesStr
        .split(",")
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean);
    }
    createMutation.mutate(filters);
  };

  return (
    <>
      <PageHeader
        title="Training data exports"
        description={
          <>
            Company classifications made by the current <code>company_classifier</code> prompt,
            exported as a zip of JSONL chat-format fine-tuning data (90/10 train/val split by
            company) for TRL, axolotl or unsloth
          </>
        }
        actions={
          <Button variant="contained" startIcon={<Plus />} onClick={() => setCreateOpen(true)}>
            New export
          </Button>
        }
      />
      <TableCard loading={isLoading}>
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Created</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Rows</TableCell>
              <TableCell align="right">Train / val</TableCell>
              <TableCell>Prompt hash</TableCell>
              <TableCell>Filters</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(exports ?? []).map((e) => (
              <TableRow key={e.id} hover>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <FormattedDate value={e.created_at} format="datetime" />
                </TableCell>
                <TableCell>
                  <StatusChip kind="job" value={e.status} />
                  {isJobActive(e.status) && e.progress_processed != null && (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", display: "block", mt: 0.5 }}
                    >
                      {formatNumber(e.progress_processed)} processed
                      {" · "}
                      {formatNumber(e.progress_written ?? 0)} written
                      {" · "}
                      {formatNumber(e.progress_skipped ?? 0)} skipped
                    </Typography>
                  )}
                  {e.error && (
                    <Typography variant="caption" sx={{ color: "error.main", display: "block" }}>
                      {e.error}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{e.row_count != null ? formatNumber(e.row_count) : "—"}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {e.train_count != null && e.val_count != null
                    ? `${formatNumber(e.train_count)} / ${formatNumber(e.val_count)}`
                    : "—"}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Tooltip title={e.prompt_hash}>
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                    >
                      {e.prompt_hash.slice(0, 12)}…
                    </Typography>
                  </Tooltip>
                  <CopyButton text={e.prompt_hash} label="Copy prompt hash" sx={{ ml: 0.5 }} />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: "monospace", color: "text.secondary" }}
                  >
                    {JSON.stringify(e.filters)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {e.status === "completed" && (
                    <Button
                      size="small"
                      variant="outlined"
                      href={api.trainingDataExportDownloadUrl(e.id)}
                    >
                      Download
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {exports?.length === 0 && (
              <EmptyTableRow colSpan={7}>No exports yet. Start one with New export.</EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      </TableCard>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>New training data export</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              The export runs in the background. Once complete the row gets
              a Download button.
            </Typography>
            <TextField
              fullWidth
              label="Max rows (optional)"
              type="number"
              value={maxRowsStr}
              onChange={(e) => setMaxRowsStr(e.target.value)}
              helperText="Cap output. Useful for test runs. Leave empty for unlimited (capped at 500k)."
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Countries (optional)"
              value={countriesStr}
              onChange={(e) => setCountriesStr(e.target.value)}
              helperText="Comma-separated ISO codes, e.g. NO,SE,FI,DK"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            loading={createMutation.isPending}
          >
            Start export
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default TrainingDataExportsPage;
